import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { isProcessing, isReady, validateLocalFile } from "@/lib/files";
import { uploadFile } from "@/lib/uploads";

const newKey = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

// Files being uploaded/processed, either for a chat message (composer) or the Files page.
export function useUploads({ initial = [] } = {}) {
  const qc = useQueryClient();
  const [items, setItems] = useState(() => initial.map((f) => ({ key: f.id, id: f.id, name: f.name, kind: f.kind, status: f.status, ai_readable: f.ai_readable })));
  const alive = useRef(true);
  useEffect(() => () => (alive.current = false), []);

  const patch = useCallback((key, values) => {
    if (alive.current) setItems((list) => list.map((it) => (it.key === key ? { ...it, ...values } : it)));
  }, []);

  const addFiles = useCallback(
    (fileList) => {
      Array.from(fileList || []).forEach(async (file) => {
        const key = newKey();
        const problem = validateLocalFile(file);
        setItems((list) => [...list, { key, name: file.name, size: file.size, status: problem ? "error" : "uploading", progress: 0, error: problem }]);
        if (problem) return;
        try {
          const record = await uploadFile(file, { onProgress: (p) => patch(key, { progress: p }) });
          patch(key, { ...record, key, progress: 1 });
          qc.invalidateQueries({ queryKey: ["files"] });
        } catch (e) {
          patch(key, { status: "error", error: e.message });
        }
      });
    },
    [patch, qc],
  );

  const addExisting = useCallback((files) => {
    setItems((list) => [...list, ...files.filter((f) => !list.some((it) => it.id === f.id)).map((f) => ({ ...f, key: f.id }))]);
  }, []);

  const remove = useCallback((key) => setItems((list) => list.filter((it) => it.key !== key)), []);
  const clear = useCallback(() => setItems([]), []);

  const pendingIds = items.filter((it) => it.id && isProcessing(it)).map((it) => it.id).join(",");
  useEffect(() => {
    if (!pendingIds) return undefined;
    const timer = setInterval(() => {
      pendingIds.split(",").forEach(async (id) => {
        try {
          const { file } = await api.get(`/files/${id}`);
          if (!isProcessing(file) && alive.current) {
            setItems((list) => list.map((it) => (it.id === id ? { ...it, ...file, key: it.key } : it)));
            qc.invalidateQueries({ queryKey: ["files"] });
          }
        } catch {
          // Keep polling; a transient error shouldn't drop the attachment.
        }
      });
    }, 1500);
    return () => clearInterval(timer);
  }, [pendingIds, qc]);

  const ready = items.every(isReady);
  const busy = items.some((it) => it.status === "uploading" || isProcessing(it));
  const attachable = items.filter(isReady).map(({ id, name, kind }) => ({ id, name, kind }));
  return { items, addFiles, addExisting, remove, clear, ready, busy, attachable };
}
